/**
 * Chat Authentication Context (Admin)
 * Bridges chat components with the existing Admin frontend auth system.
 * Reads authToken from localStorage and manages admin socket lifecycle.
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { initChatSocket, disconnectChatSocket } from "../services/chatSocket";

const ChatAuthContext = createContext(null);

export const useChatAuth = () => {
  const context = useContext(ChatAuthContext);
  if (!context) {
    throw new Error("useChatAuth must be used within ChatAuthProvider");
  }
  return context;
};

export const ChatAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load admin token from localStorage on mount (same keys as main admin app)
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");
    const storedUser = localStorage.getItem("user");

    if (storedToken) {
      setToken(storedToken);

      // Create admin user object
      setUser(JSON.parse(storedUser));

      // Initialize chat socket connection
      initChatSocket(storedToken);
    }

    setLoading(false);
  }, []);

  // Listen for token changes
  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = localStorage.getItem("authToken");
      if (newToken && newToken !== token) {
        setToken(newToken);
        setUser({ id: "admin", role: "admin" });
        initChatSocket(newToken);
      } else if (!newToken) {
        setToken(null);
        setUser(null);
        disconnectChatSocket();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
  };

  return (
    <ChatAuthContext.Provider value={value}>
      {children}
    </ChatAuthContext.Provider>
  );
};

export default ChatAuthContext;
